import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Layout } from './components/Layout';
import { session } from './services/auth';

import { LoginPage } from './pages/LoginPage';
import { OverviewPage } from './pages/OverviewPage';
import { MediaListPage } from './pages/MediaListPage';
import { MediaDetailPage } from './pages/MediaDetailPage';
import { ClassesPage } from './pages/ClassesPage';
import { ClassDetailPage } from './pages/ClassDetailPage';
import { CreatorsPage } from './pages/CreatorsPage';
import { PayoutsPage } from './pages/PayoutsPage';
import { CachePage } from './pages/CachePage';
import { SettingsPage } from './pages/SettingsPage';
import { UsersPage } from './pages/UsersPage';
import { UserDetailPage } from './pages/UserDetailPage';
import { ModerationPage } from './pages/ModerationPage';
import { BroadcastsPage } from './pages/BroadcastsPage';
import { RequestsPage } from './pages/RequestsPage';
import { AuditPage } from './pages/AuditPage';
import { AccountsPage } from './pages/AccountsPage';
import { WatermarkPage } from './pages/WatermarkPage';
import { FraudPage } from './pages/FraudPage';
import { PlaybackErrorsPage } from './pages/PlaybackErrorsPage';

function RequireAuth({ children }: { children: ReactNode }) {
  const loc = useLocation();
  if (!session.token) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (session.user?.mustChangePassword && loc.pathname !== '/password') {
    return <Navigate to="/password" replace />;
  }
  return <>{children}</>;
}

function RequireAdmin({ children }: { children: ReactNode }) {
  if (!session.isAdmin()) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<RequireAuth><Layout /></RequireAuth>}>
        <Route index element={<OverviewPage />} />
        <Route path="media" element={<MediaListPage mediaType="AUDIO" />} />
        <Route path="media/:id" element={<MediaDetailPage />} />
        <Route path="videos" element={<MediaListPage mediaType="VIDEO" />} />
        <Route path="classes" element={<RequireAdmin><ClassesPage /></RequireAdmin>} />
        <Route path="classes/:id" element={<RequireAdmin><ClassDetailPage /></RequireAdmin>} />
        <Route path="creators" element={<RequireAdmin><CreatorsPage /></RequireAdmin>} />
        <Route path="payouts" element={<RequireAdmin><PayoutsPage /></RequireAdmin>} />
        <Route path="cache" element={<RequireAdmin><CachePage /></RequireAdmin>} />
        <Route path="settings" element={<RequireAdmin><SettingsPage /></RequireAdmin>} />
        <Route path="users" element={<RequireAdmin><UsersPage /></RequireAdmin>} />
        <Route path="users/:id" element={<RequireAdmin><UserDetailPage /></RequireAdmin>} />
        <Route path="moderation" element={<RequireAdmin><ModerationPage /></RequireAdmin>} />
        <Route path="broadcasts" element={<RequireAdmin><BroadcastsPage /></RequireAdmin>} />
        <Route path="requests" element={<RequireAdmin><RequestsPage /></RequireAdmin>} />
        <Route path="audit" element={<RequireAdmin><AuditPage /></RequireAdmin>} />
        <Route path="fraud" element={<RequireAdmin><FraudPage /></RequireAdmin>} />
        <Route path="playback-errors" element={<RequireAdmin><PlaybackErrorsPage /></RequireAdmin>} />
        <Route path="accounts" element={<RequireAdmin><AccountsPage /></RequireAdmin>} />
        <Route path="watermark" element={<RequireAdmin><WatermarkPage /></RequireAdmin>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
